/**
 * /people/probation-watch — Session 3a PR 2.
 *
 * Replaces the RegistryDrivenPage placeholder with the bespoke
 * Probation Watch implementation per Session 3a §4.1.
 *
 * Owning agent: Tashghil.
 * AI surfaces (per §0.1 — every screen has at least 3):
 *   1. AiBriefPanel mounted in WorkspaceShell aiPanelSlot (live fetch)
 *   2. InlineAISuggestion cards above the DataTable
 *   3. TraceableValue wrapping `days_remaining` + `probation_end_date`
 *
 * CRUD: + Schedule Check-in primary CTA + row-hover Confirm /
 * Terminate / Extend actions per §4.1.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Plus,
  ShieldAlert,
  UserRoundCheck,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  ComingSoonBadge,
  EntityDrawer,
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import { showAlertTrace } from "./_session3aHelpers";
import { api, ApiError } from "../lib/api";
import type { ProbationEndingSoonItem } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";

const DAYS_WINDOWS = [7, 14, 30, 60] as const;

function _daysToneClass(d: number): string {
  if (d <= 14) return "bg-rose-100 text-rose-800";
  if (d <= 30) return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

function ScheduleCheckInDrawer({
  legalEntityId,
  prefillEmployeeId,
  prefillEmployeeName,
  onClose,
  onScheduled,
}: {
  legalEntityId: string;
  prefillEmployeeId?: string;
  prefillEmployeeName?: string;
  onClose: () => void;
  onScheduled: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    employee_id: prefillEmployeeId ?? "",
    employee_name: prefillEmployeeName ?? "",
    check_in_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [error, setError] = useState<string | null>(null);
  // The actual scheduling persists as an employee_event with
  // event_type='probation_check_in'. v1 ships this as a draft
  // event so HR + the manager have it in the timeline; the
  // calendar invitation lands when Twilio + email integrations
  // are provisioned.
  const submit = useMutation({
    mutationFn: () =>
      api.scheduleProbationCheckIn({
        legal_entity_id: legalEntityId,
        employee_id: form.employee_id,
        check_in_date: form.check_in_date,
        notes: form.notes,
      }),
    onSuccess: () => {
      onScheduled();
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.detail : String(e));
    },
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t(
        "people.probation_watch.cta.schedule_checkin",
        "Schedule probation check-in",
      )}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              submit.mutate();
            }}
            disabled={!form.employee_id || !form.check_in_date}
            className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("people.probation_watch.cta.schedule_submit", "Schedule")}
          </button>
        </div>
      }
    >
      <div className="space-y-4 p-4 text-sm">
        <div className="rounded-lg border border-[#e9d5ff] bg-[#f5f3ff] px-3 py-2 text-xs text-[#6b21a8]">
          ✦{" "}
          {t(
            "people.probation_watch.checkin_hint",
            "Tashghil will notify the employee and their manager via Khidmat once Twilio is provisioned.",
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("people.probation_watch.field.employee", "Employee")}
          </label>
          <input
            value={form.employee_name || form.employee_id}
            onChange={(e) =>
              setForm({ ...form, employee_id: e.target.value })
            }
            placeholder="00000000-0000-0000-0000-000000000000"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
            disabled={!!prefillEmployeeId}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("people.probation_watch.field.check_in_date", "Check-in date")}
          </label>
          <input
            type="date"
            value={form.check_in_date}
            onChange={(e) =>
              setForm({ ...form, check_in_date: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("people.probation_watch.field.notes", "Notes (optional)")}
          </label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            placeholder={t(
              "people.probation_watch.notes_placeholder",
              "What's the focus of this check-in?",
            )}
          />
        </div>
        {error && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

export function ProbationWatchPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [daysWindow, setDaysWindow] = useState<number>(30);
  const [showDrawer, setShowDrawer] = useState(false);
  const [prefillEmp, setPrefillEmp] = useState<
    { id: string; name: string } | undefined
  >();

  const list = useQuery({
    queryKey: ["probation-ending-soon", legalEntityId, daysWindow],
    queryFn: () =>
      api.getProbationEndingSoon(legalEntityId ?? "", daysWindow),
    enabled: !!legalEntityId,
  });

  const items: ProbationEndingSoonItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const critical = items.filter((r) => r.days_remaining <= 7).length;
    const attention = items.filter(
      (r) => r.days_remaining > 7 && r.days_remaining <= 14,
    ).length;
    return {
      total: items.length,
      attention,
      critical,
      critical_employee: items.find((r) => r.days_remaining <= 7),
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
      <PageHeader
        title={t("people.probation_watch.title", "Probation Watch")}
        subtitle={t(
          "people.probation_watch.subtitle",
          "Employees in probation period with action-due dates. Tashghil tracks check-in cadence and surfaces deadlines.",
        )}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {DAYS_WINDOWS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDaysWindow(d)}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                daysWindow === d
                  ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t(
                `people.probation_watch.filter.ending_in_${d}_days`,
                `Ending in ${d} days`,
              )}
              {d === 7 && counts.critical > 0 && (
                <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-semibold text-white">
                  {counts.critical}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            setPrefillEmp(undefined);
            setShowDrawer(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Plus className="h-4 w-4" />
          {t(
            "people.probation_watch.cta.schedule_checkin",
            "Schedule check-in",
          )}
        </button>
      </div>

      {/* Inline AI suggestions per §3.2 */}
      {counts.critical > 0 && counts.critical_employee && (
        <InlineAISuggestion
          agent="tashghil"
          icon={<AlertTriangle className="h-4 w-4" />}
          severity="critical"
          headline={t(
            "people.probation_watch.suggestion.critical_headline",
            `${counts.critical_employee.employee_first_name_en} ${counts.critical_employee.employee_last_name_en}'s probation ends in ${counts.critical_employee.days_remaining} days.`,
          )}
          body={t(
            "people.probation_watch.suggestion.critical_body",
            "No check-in recorded yet. Tashghil suggests scheduling one now.",
          )}
          primaryAction={{
            label: t(
              "people.probation_watch.suggestion.schedule_now",
              "Schedule now",
            ),
            onClick: () => {
              const emp = counts.critical_employee!;
              setPrefillEmp({
                id: emp.employee_id,
                name: `${emp.employee_first_name_en} ${emp.employee_last_name_en}`,
              });
              setShowDrawer(true);
            },
          }}
          suggestionKey={`probation-critical-${counts.critical_employee.employee_id}`}
        />
      )}
      {counts.total === 0 && (
        <InlineAISuggestion
          agent="tashghil"
          icon={<UserRoundCheck className="h-4 w-4" />}
          severity="info"
          headline={t(
            "people.probation_watch.suggestion.empty_headline",
            "No probations ending in this window.",
          )}
          body={t(
            "people.probation_watch.suggestion.empty_body",
            "Tashghil notes: workforce is past the probation stage for this period.",
          )}
          primaryAction={{
            label: t(
              "people.probation_watch.suggestion.see_new_joiners",
              "See new joiners",
            ),
            onClick: () => {
              window.location.assign("/people/new-joiners");
            },
          }}
          suggestionKey="probation-empty"
        />
      )}

      {list.isLoading && <Loading />}
      {list.isError && (
        <ErrorPanel message={(list.error as Error).message} />
      )}
      {items.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken">
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                <th className="px-3 py-2">
                  {t("people.probation_watch.col.employee", "Employee")}
                </th>
                <th className="px-3 py-2">
                  {t("people.probation_watch.col.hire_date", "Hire date")}
                </th>
                <th className="px-3 py-2">
                  {t(
                    "people.probation_watch.col.probation_end",
                    "Probation end",
                  )}
                </th>
                <th className="px-3 py-2">
                  {t(
                    "people.probation_watch.col.days_remaining",
                    "Days remaining",
                  )}
                </th>
                <th className="px-3 py-2">
                  {t("people.probation_watch.col.actions", "Actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.contract_id}
                  className="group border-t border-border-subtle hover:bg-surface-sunken/40"
                >
                  <td className="px-3 py-2">
                    <Link
                      to={`/people/employees/${row.employee_id}`}
                      className="font-medium text-[#2260C9] hover:underline"
                    >
                      {row.employee_first_name_en}{" "}
                      {row.employee_last_name_en}
                    </Link>
                    <div className="text-xs text-text-tertiary">
                      {row.employee_code}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-text-secondary">
                    <span dir="ltr">{row.start_date}</span>
                  </td>
                  <td className="px-3 py-2 text-text-secondary">
                    <TraceableValue
                      onTraceClick={() => {
                        showAlertTrace(
                          t(
                            "people.probation_watch.trace.probation_end",
                            `Probation end = start_date (${row.start_date}) + probation_period_days. The contract carries probation_period_days; M12 stamps this column at create time.`,
                          ),
                        );
                      }}
                    >
                      <span dir="ltr">{row.probation_end_date}</span>
                    </TraceableValue>
                  </td>
                  <td className="px-3 py-2">
                    <TraceableValue
                      onTraceClick={() => {
                        showAlertTrace(
                          t(
                            "people.probation_watch.trace.days_remaining",
                            `days_remaining = probation_end_date (${row.probation_end_date}) − today. Calendar days per Saudi Labor Law convention.`,
                          ),
                        );
                      }}
                    >
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_daysToneClass(row.days_remaining)}`}
                      >
                        {row.days_remaining}
                      </span>
                    </TraceableValue>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                      <ComingSoonBadge
                        feature="probation-confirm"
                        expectedRelease={t(
                          "people.probation_watch.coming_release",
                          "M3X.WORKFLOW",
                        )}
                      >
                        <CheckCircle2 className="me-1 inline h-3 w-3" />
                        {t("people.probation_watch.action.confirm", "Confirm")}
                      </ComingSoonBadge>
                      <button
                        type="button"
                        title={t(
                          "people.probation_watch.action.terminate",
                          "Terminate (probation_failure)",
                        )}
                        className="rounded border border-rose-300 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 hover:bg-rose-50"
                        onClick={() => {
                          window.location.assign(
                            `/operations/eos?initiate_for=${row.employee_id}&reason=probation_failure`,
                          );
                        }}
                      >
                        <XCircle className="me-1 inline h-3 w-3" />
                        {t(
                          "people.probation_watch.action.terminate",
                          "Terminate",
                        )}
                      </button>
                      <ComingSoonBadge
                        feature="probation-extend"
                        expectedRelease={t(
                          "people.probation_watch.coming_release",
                          "M3X.WORKFLOW",
                        )}
                      >
                        <CalendarClock className="me-1 inline h-3 w-3" />
                        {t("people.probation_watch.action.extend", "Extend")}
                      </ComingSoonBadge>
                      <button
                        type="button"
                        title={t(
                          "people.probation_watch.action.schedule_checkin",
                          "Schedule check-in",
                        )}
                        className="rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        onClick={() => {
                          setPrefillEmp({
                            id: row.employee_id,
                            name: `${row.employee_first_name_en} ${row.employee_last_name_en}`,
                          });
                          setShowDrawer(true);
                        }}
                      >
                        <ShieldAlert className="me-1 inline h-3 w-3" />
                        {t(
                          "people.probation_watch.action.checkin",
                          "Check-in",
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!list.isLoading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
          {t(
            "people.probation_watch.empty",
            "No probations active in this period. Tashghil suggests: open the New Joiners tab to see this quarter's onboarding load.",
          )}
        </div>
      )}

      {showDrawer && legalEntityId && (
        <ScheduleCheckInDrawer
          legalEntityId={legalEntityId}
          prefillEmployeeId={prefillEmp?.id}
          prefillEmployeeName={prefillEmp?.name}
          onClose={() => setShowDrawer(false)}
          onScheduled={() =>
            qc.invalidateQueries({ queryKey: ["probation-ending-soon"] })
          }
        />
      )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t(
              "people.probation_watch.title",
              "Probation Watch",
            ),
            window_label: `next ${daysWindow} days`,
          }}
        />
      </aside>
    </div>
  );
}
